import React, { useEffect, useState } from "react";
import { api, formatRupiah } from "../lib/api";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

function ListEditor({ endpoint, label, tid }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = async () => { const r = await api.get(endpoint); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const add = async () => { if(!name)return; await api.post(endpoint, { name }); setName(""); toast.success("Ditambahkan"); load(); };
  return (
    <div className="space-y-3" data-testid={tid}>
      <div className="flex gap-2">
        <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder={`+ Tambah ${label}`}/>
        <Button onClick={add} className="brand-btn"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it)=><Badge key={it.id} variant="outline" className="text-sm py-1">{it.name}</Badge>)}
      </div>
    </div>
  );
}

function StageEditor() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name:"", probability:20, order:99 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name:"", probability:0, order:0 });
  const load = async () => { const r = await api.get("/settings/stages"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if(!form.name) return;
    try { await api.post("/settings/stages", form); setForm({name:"",probability:20,order:99}); toast.success("Stage ditambahkan"); load(); }
    catch(e){ toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const startEdit = (s) => { setEditingId(s.id); setEditForm({ name: s.name, probability: s.probability, order: s.order }); };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    if (!editForm.name) return;
    try {
      await api.patch(`/settings/stages/${id}`, editForm);
      toast.success("Stage diperbarui"); setEditingId(null); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Hapus stage "${s.name}"?`)) return;
    try { await api.delete(`/settings/stages/${s.id}`); toast.success("Stage dihapus"); load(); }
    catch(e){ toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        <Input placeholder="Nama Stage" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="stage-name-input"/>
        <Input placeholder="Probabilitas" type="number" value={form.probability} onChange={(e)=>setForm({...form,probability:Number(e.target.value)})} data-testid="stage-prob-input"/>
        <Input placeholder="Urutan" type="number" value={form.order} onChange={(e)=>setForm({...form,order:Number(e.target.value)})} data-testid="stage-order-input"/>
        <Button onClick={add} className="brand-btn" data-testid="add-stage-btn"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </div>
      <div className="grid gap-2">
        {items.sort((a,b)=>a.order-b.order).map((s)=>(
          <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`stage-row-${s.id}`}>
            {editingId === s.id ? (
              <>
                <Input className="flex-1" value={editForm.name} onChange={(e)=>setEditForm({...editForm,name:e.target.value})} data-testid={`stage-edit-name-${s.id}`}/>
                <Input type="number" className="w-28" value={editForm.probability} onChange={(e)=>setEditForm({...editForm,probability:Number(e.target.value)})} data-testid={`stage-edit-prob-${s.id}`}/>
                <Input type="number" className="w-20" value={editForm.order} onChange={(e)=>setEditForm({...editForm,order:Number(e.target.value)})} data-testid={`stage-edit-order-${s.id}`}/>
                <button onClick={()=>saveEdit(s.id)} className="text-emerald-700 hover:text-emerald-900" title="Simpan" data-testid={`stage-save-${s.id}`}><Check className="w-4 h-4"/></button>
                <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-700" title="Batal"><X className="w-4 h-4"/></button>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <span className="font-medium">{s.name}</span>
                  <span className="text-xs text-slate-500 ml-2">#{s.order}</span>
                </div>
                <Badge variant="outline">{s.probability}%</Badge>
                <button onClick={()=>startEdit(s)} className="text-slate-500 hover:text-emerald-700" title="Edit" data-testid={`stage-edit-${s.id}`}><Pencil className="w-4 h-4"/></button>
                <button onClick={()=>del(s)} className="text-slate-400 hover:text-rose-600" title="Hapus" data-testid={`stage-del-${s.id}`}><Trash2 className="w-4 h-4"/></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductEditor() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name:"", price:0, category:"", status:"active" });
  const load = async () => { const r = await api.get("/settings/products"); setItems(r.data); };
  useEffect(()=>{load();},[]);
  const add = async () => { if(!form.name)return; await api.post("/settings/products", { ...form, price: Number(form.price) }); setForm({name:"",price:0,category:"",status:"active"}); load(); };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-2">
        <Input placeholder="Nama Produk" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/>
        <Input placeholder="Kategori" value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})}/>
        <Input type="number" placeholder="Harga" value={form.price} onChange={(e)=>setForm({...form,price:e.target.value})}/>
        <Select value={form.status} onValueChange={(v)=>setForm({...form,status:v})}>
          <SelectTrigger><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent>
        </Select>
        <Button onClick={add} className="brand-btn"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </div>
      <div className="grid gap-2">
        {items.map((p)=>(
          <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border">
            <div><span className="font-medium">{p.name}</span> <span className="text-xs text-slate-500 ml-2">{p.category}</span></div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-emerald-700">{formatRupiah(p.price)}</span>
              <Badge variant="outline">{p.status}</Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ full_name:"", email:"", password:"", role:"sales" });
  const load = async () => { const r = await api.get("/users"); setItems(r.data); };
  useEffect(()=>{load();},[]);
  const add = async () => {
    try { await api.post("/users", form); toast.success("User ditambahkan"); setForm({full_name:"",email:"",password:"",role:"sales"}); load(); }
    catch(e){ toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Hapus user "${u.full_name}"?`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("User dihapus"); load(); }
    catch(e){ toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-2">
        <Input placeholder="Nama" value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})}/>
        <Input placeholder="Email" type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/>
        <Input placeholder="Password" type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})}/>
        <Select value={form.role} onValueChange={(v)=>setForm({...form,role:v})}>
          <SelectTrigger><SelectValue/></SelectTrigger>
          <SelectContent>{["owner","admin","sales_manager","sales"].map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={add} className="brand-btn" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </div>
      <div className="grid gap-2">
        {items.map((u)=>(
          <div key={u.user_id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`user-row-${u.user_id}`}>
            <div>
              <div className="font-medium">{u.full_name}</div>
              <div className="text-xs text-slate-500">{u.email}</div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="capitalize">{u.role.replace("_"," ")}</Badge>
              {u.role !== "owner" && (
                <button onClick={()=>del(u)} className="text-slate-400 hover:text-rose-600" title="Hapus user" data-testid={`del-user-${u.user_id}`}>
                  <Trash2 className="w-4 h-4"/>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Settings() {
  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold" style={{fontFamily:"Outfit"}}>Pengaturan</h1>
        <p className="text-sm text-slate-500">Kustomisasi workspace CRM Anda.</p>
      </div>

      <Card className="p-4">
        <Tabs defaultValue="users">
          <TabsList className="flex-wrap">
            <TabsTrigger value="users" data-testid="set-tab-users">Users</TabsTrigger>
            <TabsTrigger value="sources" data-testid="set-tab-sources">Lead Sources</TabsTrigger>
            <TabsTrigger value="stages" data-testid="set-tab-stages">Pipeline Stages</TabsTrigger>
            <TabsTrigger value="industries" data-testid="set-tab-industries">Industri</TabsTrigger>
            <TabsTrigger value="products" data-testid="set-tab-products">Produk / Layanan</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="pt-4"><UsersPanel/></TabsContent>
          <TabsContent value="sources" className="pt-4"><ListEditor endpoint="/settings/sources" label="Sumber" tid="sources-editor"/></TabsContent>
          <TabsContent value="stages" className="pt-4"><StageEditor/></TabsContent>
          <TabsContent value="industries" className="pt-4"><ListEditor endpoint="/settings/industries" label="Industri" tid="industries-editor"/></TabsContent>
          <TabsContent value="products" className="pt-4"><ProductEditor/></TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
